/**
 * ArchLang v0.10 — grid (cross-tab) representation data model (engine layer).
 *
 * `computeGridData(view, plan, ctx)` turns a `view` carrying a `grid` clause into
 * a **`GridData`**: a 2D label grid where the row/col axes group the shown modules
 * by a getter and each cell LISTS the modules at that (row, col) intersection
 * (co-membership — a module whose row-label is `rl` AND col-label is `cl` lands in
 * cell `(rl, cl)`). This is distinct from the `matrix` DSM, whose cells are call
 * edges between the axis groups. An optional `color` getter tints each member by a
 * third field (categorical `colorize` palette + legend). Pure over the derived
 * model — no scene types, no rendering.
 *
 * ## Multi-membership
 * A module with a list-valued aspect axis (e.g. two `@@team` values) joins each
 * group — it appears in every matching cell. Axes and cell members are sorted, so
 * the projection is a pure function of `(view, plan, model)`.
 */

import type { GridClause, ViewDeclV2 } from "./v010-ast.js";
import type { EvalContext } from "./selector-eval.js";
import { buildAxis, axisValuesOf } from "./view-axis.js";
import { assignColorize, type ColorLegendEntry } from "./view-style.js";
import type { ViewProjectionPlan } from "./view-projection.js";

// =============================================================================
// Shape
// =============================================================================

/** One member module of a cell. */
export interface GridMember {
  /** Qualified module key (the color map / legend key). */
  readonly key: string;
  /** Short display label (the last path segment). */
  readonly label: string;
}

/** One cross-tab cell — the modules at a (row, col) intersection (empty = blank). */
export interface GridCell {
  readonly members: readonly GridMember[];
}

/** The full cross-tab projection — label axes + a `cells[row][col]` grid. */
export interface GridData {
  /** Row axis labels (module keys, or grouped getter values) — ordered. */
  readonly axisRows: readonly string[];
  /** Column axis labels — ordered. */
  readonly axisCols: readonly string[];
  /** `cells[rowIndex][colIndex]` — aligned to {@link axisRows} / {@link axisCols}. */
  readonly cells: readonly (readonly GridCell[])[];
  /** True when the ROW axis is a grouped getter axis (labels ≠ module keys). */
  readonly rowsGrouped: boolean;
  /** True when the COLUMN axis is a grouped getter axis. */
  readonly colsGrouped: boolean;
  /** Module key → color, when a `color` getter is present (else empty). */
  readonly colors: ReadonlyMap<string, string>;
  /** Color legend rows, when a `color` getter is present (else empty). */
  readonly legend: readonly ColorLegendEntry[];
}

// =============================================================================
// computeGridData
// =============================================================================

/**
 * Project a `view` with a `grid` clause into a {@link GridData}. Returns `null`
 * for a view that carries no `GridClause`.
 */
export function computeGridData(
  view: ViewDeclV2,
  plan: ViewProjectionPlan | null,
  ctx: EvalContext,
): GridData | null {
  const grid = view.clauses.find((c): c is GridClause => c.kind === "grid");
  if (!grid) return null;

  // Shown modules — the cross-tab universe.
  const modules = ctx.universe.nodes.filter(
    (n) => n.structuralKind === "module" && (plan ? plan.shown.has(n.key) : true),
  );

  const rowAxis = buildAxis(grid.rows, modules, ctx);
  const colAxis = buildAxis(grid.cols, modules, ctx);

  // Bucket every module into its (rowLabel, colLabel) cells. A module mapping to
  // several labels on an axis (list-valued aspect) fans to each combination.
  const buckets = new Map<string, GridMember[]>();
  for (const m of modules) {
    const rowLabels = rowAxis.labelsOf.get(m.key);
    const colLabels = colAxis.labelsOf.get(m.key);
    if (!rowLabels || !colLabels) continue; // no value on an axis → placed nowhere
    const member: GridMember = { key: m.key, label: shortLabel(m.key) };
    for (const rl of rowLabels) {
      for (const cl of colLabels) {
        const bkey = rl + " " + cl;
        const b = buckets.get(bkey) ?? [];
        b.push(member);
        buckets.set(bkey, b);
      }
    }
  }

  const cells: GridCell[][] = rowAxis.labels.map((rl) =>
    colAxis.labels.map((cl) => {
      const b = buckets.get(rl + " " + cl) ?? [];
      const members = [...b].sort((a, c) => (a.key < c.key ? -1 : a.key > c.key ? 1 : 0));
      return { members };
    }),
  );

  // `color` getter → per-module categorical color + legend (reuses colorize).
  let colors: ReadonlyMap<string, string> = new Map();
  let legend: readonly ColorLegendEntry[] = [];
  if (grid.color) {
    const perNode = new Map<string, string | undefined>();
    for (const m of modules) {
      const vals = axisValuesOf(grid.color, m, ctx);
      perNode.set(m.key, vals[0]);
    }
    const asn = assignColorize(perNode, "grid-color");
    colors = asn.colors;
    legend = asn.legend;
  }

  return {
    axisRows: rowAxis.labels,
    axisCols: colAxis.labels,
    cells,
    rowsGrouped: rowAxis.grouped,
    colsGrouped: colAxis.grouped,
    colors,
    legend,
  };
}

/** Short display label = the last path segment of a qualified module key. */
function shortLabel(key: string): string {
  const i = key.lastIndexOf(".");
  return i < 0 ? key : key.slice(i + 1);
}
